import {
  type EnvironmentId,
  type ServerConfig,
  type ServerConfigStreamEvent,
  type ServerLifecycleWelcomePayload,
  WS_METHODS,
} from "@afar-research/contracts";
import * as Option from "effect/Option";
import * as Stream from "effect/Stream";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import {
  createAtomCommandScheduler,
  createEnvironmentRpcCommand,
  createEnvironmentRpcQueryAtomFamily,
  createEnvironmentRpcSubscriptionAtomFamily,
} from "./runtime.ts";
import type { EnvironmentRegistry } from "../connection/registry.ts";

export interface ServerConfigProjection {
  readonly config: ServerConfig;
  readonly latestEvent: ServerConfigStreamEvent;
}

export function applyServerConfigProjection(
  current: Option.Option<ServerConfigProjection>,
  event: ServerConfigStreamEvent,
): Option.Option<ServerConfigProjection> {
  switch (event.type) {
    case "snapshot":
      return Option.some({
        config: event.config,
        latestEvent: event,
      });
    case "keybindingsUpdated":
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          keybindings: event.payload.keybindings,
          issues: event.payload.issues,
        },
        latestEvent: event,
      }));
    case "providerStatuses":
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          providers: event.payload.providers,
        },
        latestEvent: event,
      }));
    case "settingsUpdated":
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          settings: event.payload.settings,
        },
        latestEvent: event,
      }));
    case "environmentThemesUpdated":
      // The snapshot deliberately carries no themes, so this is the only
      // source: the stream emits the current set before any change.
      return Option.map(current, (projection) => ({
        config: {
          ...projection.config,
          environmentThemes: event.payload.themes,
        },
        latestEvent: event,
      }));
  }
}

export function projectServerConfig(
  current: Option.Option<ServerConfigProjection>,
  event: ServerConfigStreamEvent,
): readonly [Option.Option<ServerConfigProjection>, ReadonlyArray<ServerConfigProjection>] {
  const next = applyServerConfigProjection(current, event);
  return [next, Option.toArray(next)];
}

export function projectServerWelcome(
  current: Option.Option<ServerLifecycleWelcomePayload>,
  event: {
    readonly type: "welcome" | "ready";
    readonly payload: unknown;
  },
): readonly [
  Option.Option<ServerLifecycleWelcomePayload>,
  ReadonlyArray<ServerLifecycleWelcomePayload>,
] {
  if (event.type !== "welcome") {
    return [current, []];
  }
  const welcome = event.payload as ServerLifecycleWelcomePayload;
  return [Option.some(welcome), [welcome]];
}

export function createServerEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
  options: {
    readonly initialConfigValueAtom: (
      environmentId: EnvironmentId,
    ) => Atom.Atom<ServerConfig | null>;
  },
) {
  const configScheduler = createAtomCommandScheduler();
  const configConcurrency = {
    mode: "serial" as const,
    key: ({ environmentId }: { readonly environmentId: string }) => environmentId,
  };
  const providerInstanceSingleFlight = {
    mode: "singleFlight" as const,
    key: ({
      environmentId,
      input,
    }: {
      readonly environmentId: string;
      readonly input: { readonly instanceId: string };
    }) => `${environmentId}:${input.instanceId}`,
  };
  const configProjection = createEnvironmentRpcSubscriptionAtomFamily(runtime, {
    label: "environment-data:server:config-projection",
    tag: WS_METHODS.subscribeServerConfig,
    transform: (stream) =>
      stream.pipe(Stream.mapAccum(Option.none<ServerConfigProjection>, projectServerConfig)),
  });
  const emptyConfigAtom = Atom.make<ServerConfig | null>(null).pipe(
    Atom.withLabel("environment-data:server:config:empty"),
  );
  const configValueAtom = Atom.family((environmentId: EnvironmentId | null) => {
    if (environmentId === null) {
      return emptyConfigAtom;
    }
    return Atom.make((get): ServerConfig | null => {
      const projection = Option.getOrNull(
        AsyncResult.value(get(configProjection({ environmentId, input: {} }))),
      );
      return projection?.config ?? get(options.initialConfigValueAtom(environmentId));
    }).pipe(Atom.withLabel(`environment-data:server:config:${environmentId}`));
  });
  const settingsValueAtom = Atom.family((environmentId: EnvironmentId) =>
    Atom.make((get) => get(configValueAtom(environmentId))?.settings ?? null).pipe(
      Atom.withLabel(`environment-data:server:settings:${environmentId}`),
    ),
  );
  const providersValueAtom = Atom.family((environmentId: EnvironmentId) =>
    Atom.make((get) => get(configValueAtom(environmentId))?.providers ?? null).pipe(
      Atom.withLabel(`environment-data:server:providers:${environmentId}`),
    ),
  );

  return {
    configValueAtom,
    settingsValueAtom,
    providersValueAtom,
    traceDiagnostics: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:trace-diagnostics",
      tag: WS_METHODS.serverGetTraceDiagnostics,
    }),
    processDiagnostics: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:process-diagnostics",
      tag: WS_METHODS.serverGetProcessDiagnostics,
    }),
    processResourceHistory: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:process-resource-history",
      tag: WS_METHODS.serverGetProcessResourceHistory,
    }),
    providerSkills: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:provider-skills",
      tag: WS_METHODS.serverListProviderSkills,
      staleTimeMs: 0,
      idleTtlMs: 5 * 60_000,
    }),
    configProjection,
    welcome: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:server:welcome",
      tag: WS_METHODS.subscribeServerLifecycle,
      transform: (stream) =>
        stream.pipe(
          Stream.mapAccum(Option.none<ServerLifecycleWelcomePayload>, projectServerWelcome),
        ),
    }),
    providerMcp: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:server:provider-mcp",
      tag: WS_METHODS.subscribeProviderMcp,
    }),
    /** What the supervised Computer Use Service achieved; the intent lives in Settings. */
    computerUseService: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:server:computer-use-service",
      tag: WS_METHODS.computerUseServiceStatus,
    }),
    /** Tells a Cloud Workspace this connection's user has gone unused, or come back. */
    setCloudClientAway: createEnvironmentRpcCommand(runtime, {
      label: "environment-command:server:set-cloud-client-away",
      tag: WS_METHODS.serverSetCloudClientAway,
    }),
    recheckComputerUseService: createEnvironmentRpcCommand(runtime, {
      label: "environment-command:server:computer-use-recheck",
      tag: WS_METHODS.computerUseServiceRecheck,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    requestComputerUsePermissions: createEnvironmentRpcCommand(runtime, {
      label: "environment-command:server:computer-use-request-permissions",
      tag: WS_METHODS.computerUseServiceRequestPermissions,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    openComputerUsePrivacySettings: createEnvironmentRpcCommand(runtime, {
      label: "environment-command:server:computer-use-open-privacy-settings",
      tag: WS_METHODS.computerUseServiceOpenPrivacySettings,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    testMcpConnection: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:test-mcp-connection",
      tag: WS_METHODS.serverTestMcpConnection,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) => `${environmentId}:${input.name}`,
      },
    }),
    refreshProviderMcp: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:refresh-provider-mcp",
      tag: WS_METHODS.serverProviderRefreshMcp,
      concurrency: providerInstanceSingleFlight,
    }),
    // A cold transcript scan is measured in seconds, so keep the result around
    // long enough that switching windows or re-rendering does not rescan.
    usageSummary: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:server:usage-summary",
      tag: WS_METHODS.serverGetUsageSummary,
      staleTimeMs: 60_000,
    }),
    refreshUsageRates: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:refresh-usage-rates",
      tag: WS_METHODS.serverRefreshUsageRates,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId }) => environmentId,
      },
    }),
    consumeResetCredit: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:consume-reset-credit",
      tag: WS_METHODS.serverProviderConsumeResetCredit,
      concurrency: providerInstanceSingleFlight,
    }),
    refreshProviders: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:refresh-providers",
      tag: WS_METHODS.serverRefreshProviders,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId }) => environmentId,
      },
    }),
    createAppOwnedCodex: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-create-app-owned-codex",
      tag: WS_METHODS.serverProviderCreateAppOwnedCodex,
      concurrency: {
        mode: "serial",
        key: ({ environmentId }) => environmentId,
      },
    }),
    retryManagedProviderInstall: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-retry-install",
      tag: WS_METHODS.serverProviderRetryInstall,
      concurrency: {
        mode: "serial",
        key: ({ environmentId }) => environmentId,
      },
    }),
    deleteManagedProvider: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-delete",
      tag: WS_METHODS.serverProviderDelete,
      concurrency: {
        mode: "serial",
        key: ({ environmentId }) => environmentId,
      },
    }),
    startProviderBrowserLogin: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-start-browser-login",
      tag: WS_METHODS.serverProviderStartBrowserLogin,
      concurrency: providerInstanceSingleFlight,
    }),
    startProviderDeviceCodeLogin: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-start-device-code-login",
      tag: WS_METHODS.serverProviderStartDeviceCodeLogin,
      concurrency: providerInstanceSingleFlight,
    }),
    loginProviderApiKey: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-login-api-key",
      tag: WS_METHODS.serverProviderLoginApiKey,
      concurrency: providerInstanceSingleFlight,
    }),
    cancelProviderAuthentication: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-cancel-authentication",
      tag: WS_METHODS.serverProviderCancelAuthentication,
      concurrency: providerInstanceSingleFlight,
    }),
    logoutProvider: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:provider-logout",
      tag: WS_METHODS.serverProviderLogout,
      concurrency: providerInstanceSingleFlight,
    }),
    updateProvider: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:update-provider",
      tag: WS_METHODS.serverUpdateProvider,
      concurrency: providerInstanceSingleFlight,
    }),
    upsertKeybinding: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:upsert-keybinding",
      tag: WS_METHODS.serverUpsertKeybinding,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    removeKeybinding: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:remove-keybinding",
      tag: WS_METHODS.serverRemoveKeybinding,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    updateSettings: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:update-settings",
      tag: WS_METHODS.serverUpdateSettings,
      scheduler: configScheduler,
      concurrency: configConcurrency,
    }),
    signalProcess: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:server:signal-process",
      tag: WS_METHODS.serverSignalProcess,
    }),
  };
}
